import { Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { EnvironmentVariablesService } from '@kenv';
import { BrowserStorage, DataStoreService, WINDOW } from '@kservice';
import { UserType } from '@ktypes/enums';
import { JsonObject } from '@ktypes/models';
import { getQueryParamsFromString } from '@kutil';
import { BehaviorSubject } from 'rxjs';
import { filter, skipWhile, take } from 'rxjs/operators';
import { AnalyticsEvent, PAGE_MAPPING } from './analytics-event.model';
import { AnalyticsApi } from './analytics.api';
import { AnalyticEvent } from './enums/analytic-event.enum';
import { AnalyticsCategory } from './enums/analytics-category.enum';
import { AnalyticsPageName } from './enums/analytics-page-name.enum';
import { PageMappingData } from './page-mapping-info.model';

interface CalculatedEventInfo {
  appVersion: string;
  page: string;
  platformVersion: string;
  meta: Record<string, any>;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsBloc {
  private _CACHE_NAME = 'Analytics_Cache';
  private _CACHE_TO_PROCESS = 10;
  private _cachedEvents: AnalyticsEvent[] = [];
  private _inProcess = false;
  private _userReadyForAnalytics$ = new BehaviorSubject<boolean>(false);

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _analyticsApi: AnalyticsApi,
    private _browserStorage: BrowserStorage,
    private _dataStoreService: DataStoreService,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _location: Location,
    private _router: Router,
    @Inject(PAGE_MAPPING) private _pageMappingData: PageMappingData,
    @Inject(WINDOW) private _window: Window
  ) {}

  initialize() {
    // ENTER/EXIT PAGE: subscribe to router page visit nav start & end events and send page views to Analytics Api
    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart))
      .subscribe((navEvent: NavigationStart | NavigationEnd) => {
        // Default to NavigationEnd
        let page = navEvent.url;
        let event = AnalyticEvent.entering;

        if (navEvent instanceof NavigationStart) {
          page = this._router.url;
          event = AnalyticEvent.leaving;
        }
        if (page === '/') {
          page = this._location.path();
        }
        if (page.includes('dialogue')) {
          this.userReadyForAnalytics();
        }
        if (page && event && this._pageMappingData.pages.find((pages) => page.match(pages.regex))) {
          this.sendEvent(this.createEventFromPage({ page, event }));
        }
      });

    this._userReadyForAnalytics$
      .pipe(
        skipWhile((userReadyForAnalytics) => !userReadyForAnalytics),
        take(1)
      )
      .subscribe((userReadyForAnalytics) => {
        if (userReadyForAnalytics) {
          void this._processCacheEvents();
        }
      });
  }

  sendEvent(event: AnalyticsEvent) {
    if (
      this._dataStoreService.authData?.token &&
      this._dataStoreService.authData?.user?.type !== UserType.deletion &&
      this._userReadyForAnalytics$.value
    ) {
      this._uploadEvent(event);
      void this._processCacheEvents();
    } else {
      this._cacheEvent(event);
    }
  }

  // NOTE: used for tracking page to page navigation through the site; should not need to call
  // this directly, it is called by listening to the Angular Router navigation events
  createEventFromPage(event: {
    page: string;
    section?: string;
    event: AnalyticEvent;
    label?: string;
    value?: number;
    meta?: JsonObject;
  }): AnalyticsEvent {
    if (event) {
      return new AnalyticsEvent().deserialize({
        ...event,
        ...this._getCalculatedEventInfo(event),
        ...{
          url: event.page,
          category: AnalyticsCategory.page,
        },
      });
    }
    return new AnalyticsEvent().deserialize(this._getCalculatedEventInfo());
  }

  // NOTE: use this for tracking events that are not triggered by direct user interactions
  createEventFromEvent(event: {
    event: AnalyticEvent;
    label?: string;
    value?: number;
    meta?: JsonObject;
  }): AnalyticsEvent {
    return new AnalyticsEvent().deserialize({
      ...event,
      ...this._getCalculatedEventInfo(event),
      category: AnalyticsCategory.interaction,
    });
  }

  // NOTE: use this for tracking user interactions (button clicks, form changes, user selections, etc.)
  createEventFromInteraction(event: {
    page?: AnalyticsPageName;
    event: AnalyticEvent;
    label?: string;
    value?: number;
    meta?: JsonObject;
  }): AnalyticsEvent {
    return new AnalyticsEvent().deserialize({
      ...event,
      ...this._getCalculatedEventInfo(event),
      category: AnalyticsCategory.interaction,
    });
  }

  userReadyForAnalytics() {
    if (!this._userReadyForAnalytics$.value) {
      // only set it true once
      this._userReadyForAnalytics$.next(true);
    }
  }

  getDynamicPageNameIfMatch(url: string = this._router?.url): AnalyticsPageName {
    const pageInfo = this._pageMappingData?.pages?.find((pageInfo) => url?.match?.(pageInfo.regex));
    return pageInfo?.pageName;
  }

  private _getCalculatedEventInfo(input?: JsonObject): CalculatedEventInfo {
    // TODO: normalize pages like Question Sets with additional meta data in the URLs
    const normalizedPageInfo = this._pageMappingData?.pages?.find((pageInfo) => input?.page?.match?.(pageInfo.regex));
    const platformVersion = this._window.navigator.userAgent;
    const [url, ...rest] = this._router?.url?.split('?') || [];
    const queryParams = getQueryParamsFromString(rest?.join('?'));
    const meta = {
      queryParams,
      url: url || '',
      circleTag: this._environmentVariablesService.circleTag,
      product: this._environmentVariablesService.product,
      ...(normalizedPageInfo?.info || {}),
      ...(input?.meta || {}),
    };
    const appVersion = this._environmentVariablesService.appVersion;
    return {
      appVersion,
      page: normalizedPageInfo?.pageName || input?.page || '',
      platformVersion,
      meta,
    };
  }

  private _uploadEvent(event: AnalyticsEvent) {
    if (event.event && this._pageMappingData?.dontTrack?.indexOf(event.url) !== -1) {
      return;
    }
    void this._analyticsApi.post(event);
  }

  private _cacheEvent(event: AnalyticsEvent) {
    if (
      event.event &&
      this._pageMappingData?.dontTrack?.indexOf(event.url) !== -1 &&
      this._pageMappingData?.dontCache?.indexOf(event.url) !== -1
    ) {
      return;
    }
    this._cachedEvents = this._browserStorage.getObject(this._CACHE_NAME) || [];
    this._cachedEvents.push(event);
    this._browserStorage.setObject(this._CACHE_NAME, this._cachedEvents);
  }

  private async _processCacheEvents() {
    if (this._inProcess) {
      return;
    }
    this._inProcess = true;
    const typedEvents: AnalyticsEvent[] = [];
    this._cachedEvents = this._browserStorage.getObject(this._CACHE_NAME) || [];
    if (this._cachedEvents.length > 0) {
      const eventsToProcess = this._cachedEvents.splice(0, this._CACHE_TO_PROCESS);
      if (this._cachedEvents.length > 0) {
        // if there are still cached objects, update the cache minus the processed elements,
        // do immediately to reduce likelihood of race conditions sending the events multiple times
        this._browserStorage.setObject(this._CACHE_NAME, this._cachedEvents);
      }
      for (const event of eventsToProcess) {
        // only process 10 at a time to reduce network stress/connections
        typedEvents.push(new AnalyticsEvent().deserialize(event));
      }
      for (const analyticEvent of typedEvents) {
        await this._analyticsApi.post(analyticEvent);
      }
      if (this._cachedEvents.length === 0) {
        this._browserStorage.remove(this._CACHE_NAME);
      }
      this._inProcess = false;
    }
  }
}

export class MockAnalyticsBloc {
  initialize() {
    /* no-op */
  }
  sendEvent(/* event?: AnalyticsEvent */) {
    /* no-op */
  }
  createEventFromPage(event: AnalyticEvent) {
    /* no-op */
  }
  createEventFromEvent(event: AnalyticEvent) {
    /* no-op */
  }
  createEventFromInteraction(event: AnalyticEvent) {
    /* no-op */
  }
  userReadyForAnalytics() {
    /* no-op */
  }
}
